import {
  buildAnnouncementKey,
  markReminderDismissed,
  REMINDER_LEAD_MINUTES,
} from './index'
import { formatTimeRangeWithTimezone } from '@thyme/libs/src/date/formatting'
import { minutesToMs } from '@thyme/libs/src/time/convert'
import { DateTime } from 'luxon'
import { dateTimeToString } from '@/libs/date/date'
import type {
  DateOnlyUpcomingCall,
  TimedUpcomingCall,
  UpcomingCall,
} from './types'
import type { TToastMessage } from '@thyme/nashville/src/components/toast/types'
import type { useTToast } from '@thyme/nashville/src/components/toast/useTToast'

const UNKNOWN_MEMBER_NAME = 'Unknown Member'

/**
 * @param call - The call being announced, either arm of the union.
 */
function sharedToastFields(call: UpcomingCall) {
  return {
    id: buildAnnouncementKey(call),
    summary: `Scheduled call with ${call.patientName || UNKNOWN_MEMBER_NAME}`,
    actionLabel: 'Open member profile',
    actionTo: {
      name: '#patientProfile',
      params: { patientId: call.patientId },
      query: { commId: call.communicationId },
    },
  }
}

/**
 * @param call - A call already confirmed to be inside the reminder window.
 * @returns The message to pass to `useTToast().add()`.
 */
export function toTimedCallToastMessage(
  call: TimedUpcomingCall
): TToastMessage {
  return {
    ...sharedToastFields(call),
    headerTitle: `Starts in ${REMINDER_LEAD_MINUTES} minutes`,
    detail: formatTimeRangeWithTimezone(
      dateTimeToString(call.startTime.toUTC()),
      call.endTime ? dateTimeToString(call.endTime.toUTC()) : undefined
    ),
    // Tied to the lead time rather than a fixed duration, so the toast (typically)
    // auto-dismisses as the call begins.
    life: minutesToMs(REMINDER_LEAD_MINUTES),
  }
}

/**
 * @param call - An all-day call for today.
 * @returns The message to pass to `useTToast().add()`.
 */
export function toDateOnlyCallToastMessage(
  call: DateOnlyUpcomingCall
): TToastMessage {
  const now = DateTime.now()
  return {
    ...sharedToastFields(call),
    headerTitle: 'For today',
    detail: 'No call time',
    life: now.endOf('day').diff(now).toMillis(),
  }
}

/**
 * @param toast - The toast stack, i.e. `useTToast()`.
 * @param calls - The calls handed over by `useUpcomingCallReminders`.
 */
export function announceCallReminders(
  toast: ReturnType<typeof useTToast>,
  calls: UpcomingCall[]
): void {
  for (const call of calls) {
    if (call.isDateOnly) {
      toast.add(toDateOnlyCallToastMessage(call))
    } else {
      toast.add(toTimedCallToastMessage(call))
    }
  }
}

/**
 * Remembers user-dismissed toast (not auto-dismissed) so will not re-announce this session
 * Ignores PV-generated toast ids, only tracks our scheduled call string communication:datetime ids
 * @param message - The message the user dismissed.
 */
export function dismissCallReminder(message: TToastMessage): void {
  if (typeof message.id !== 'string') {
    return
  }
  markReminderDismissed(message.id)
}
